import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Clock, PlayCircle, CheckCircle2, XCircle, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function ExamPortalPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/exams/portal/tests");
      setTests(data);
    } catch (e) {
      toast.error("Failed to load exams");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startTest = async (test) => {
    try {
      const { data: session } = await api.post(`/exams/sessions/start/${test.id}`);
      nav(`/exam/attempt/${session.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cannot start test");
    }
  };

  const badge = (t) => {
    if (t.session_status === "submitted") return <span className="text-[11px] px-2 py-0.5 rounded bg-[#D1E7DD] text-[#057642] font-semibold uppercase">Completed · {t.percent}%</span>;
    if (t.status === "live") return <span className="text-[11px] px-2 py-0.5 rounded bg-[#EAF3FB] text-[#0A66C2] font-semibold uppercase">Live now</span>;
    if (t.status === "closed") return <span className="text-[11px] px-2 py-0.5 rounded bg-[#F8D7DA] text-[#CC1016] font-semibold uppercase">Closed</span>;
    return <span className="text-[11px] px-2 py-0.5 rounded bg-[#FFF3CD] text-[#915907] font-semibold uppercase">Scheduled</span>;
  };

  return (
    <div className="min-h-screen bg-[#F4F2EE]">
      {/* Exam Portal header — separate from main sidebar */}
      <header className="bg-[#0A66C2] text-white sticky top-0 z-40" data-testid="exam-portal-header">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-white/15 flex items-center justify-center font-bold">E</div>
            <div>
              <div className="text-[15px] font-bold">Exam Portal</div>
              <div className="text-[11px] text-white/75">CoachERP · Online Testing</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold" data-testid="exam-user-name">{user?.name}</div>
              <div className="text-[11px] text-white/75">{user?.email}</div>
            </div>
            <Link to="/dashboard" data-testid="back-to-erp-link" className="text-xs px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 inline-flex items-center gap-1">
              <ArrowLeft size={12}/> Back to ERP
            </Link>
            <button onClick={async () => { await logout(); nav("/login"); }} data-testid="exam-logout-btn" className="text-xs px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25">Sign out</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 fade-up">
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-3xl font-semibold text-[#000000E6] tracking-tight">Available exams</h1>
            <p className="text-sm text-[#00000099] mt-1">Attempt scheduled tests. Ensure your camera and microphone permissions are enabled.</p>
          </div>
          <div className="hidden md:flex items-center gap-2 text-xs text-[#00000099] bg-white border border-[#E0DFDC] rounded-full px-4 py-2">
            <ShieldCheck size={14} className="text-[#057642]"/> Proctored environment
          </div>
        </div>

        {loading && <div className="text-[#00000099]">Loading…</div>}

        <div className="space-y-4">
          {!loading && tests.length === 0 && (
            <div className="linkedin-card p-8 text-center text-[#00000099]">No exams scheduled for you yet.</div>
          )}
          {tests.map((t) => (
            <div key={t.id} className="linkedin-card p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`exam-card-${t.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-lg font-semibold text-[#000000E6]">{t.name}</h3>
                  {badge(t)}
                </div>
                <div className="text-xs text-[#00000099] mt-1">{t.description}</div>
                <div className="mt-3 flex flex-wrap gap-4 text-xs text-[#00000099]">
                  <span className="inline-flex items-center gap-1"><Clock size={12}/> {t.duration_min} min</span>
                  <span>{t.question_count} questions</span>
                  <span>{t.total_marks} marks</span>
                  <span>{(t.sections || []).length} sections</span>
                  <span>{t.negative_marking ? "Negative marking" : "No negative"}</span>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                {t.session_status === "submitted" ? (
                  <Link to={`/exam/result/${t.id}`} data-testid={`view-result-${t.id}`} className="btn-primary inline-flex items-center gap-2"><CheckCircle2 size={14}/> View result</Link>
                ) : t.status === "closed" ? (
                  <span className="text-xs text-[#00000066]"><XCircle size={12} className="inline mr-1"/> Test window closed</span>
                ) : t.status === "live" ? (
                  <button onClick={() => startTest(t)} data-testid={`start-exam-${t.id}`} className="btn-primary inline-flex items-center gap-2"><PlayCircle size={16}/> {t.session_status === "in_progress" ? "Resume" : "Start test"}</button>
                ) : (
                  <span className="text-xs text-[#00000066]">Opens {new Date(t.start_at).toLocaleString()}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
